import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { CallStatus } from '@marnic/shared';
import type {
  CallReportRow,
  EntityStatisticsReport,
  MonthlyCallVolumeReport,
  OverviewReport,
  PaginatedReport,
  ReportKind,
  ReportSummary,
  UserStatisticRow,
  UserStatisticsReport,
} from '@marnic/shared/reports';
import { api } from '../lib/api';
import { CallRecordingPlayer } from '../calls/CallRecordingPlayer';
import { ChartLegend, DailyOverviewChart, HourlyStackedBars, LineChart, MonthlyVolumeChart, TalkTimeBars, UserDurationBars, UserStackedBars, WeekdayBars, monthlyVolumeColors } from '../reports/Charts';
import { ReportControls } from '../reports/ReportControls';
import { ReportTabs } from '../reports/ReportTabs';
import { useReportState } from '../reports/useReportState';
import { CUMULATIVE_BUCKETS, TalkTimeBreakdown } from '../reports/TalkTimeBreakdown';
import { talkTimeAtLeast } from '@marnic/shared';
import type { TalkTimeBucketKey } from '@marnic/shared';
import { buildReportSearch, clearFilters, countActiveFilters, formatDuration, reportLabel, type ReportQueryState } from '../reports/report-utils';
import { ChartSkeleton, SummaryCardsSkeleton, TableSkeleton } from '../components/Skeleton';
import { ErrorState } from '../components/ErrorState';
import { QualityScoresView } from '../reports/QualityScores';

export function Reports(): JSX.Element {
  return (
    <Routes>
      <Route index element={<Navigate to="overview" replace />} />
      <Route path="overview" element={<OverviewReportPage />} />
      <Route path="call_history" element={<CallHistoryReportPage />} />
      <Route path="call_recordings" element={<CallRecordingsReportPage />} />
      <Route path="volume" element={<MonthlyVolumeReportPage />} />
      <Route path="user_reports" element={<UserStatisticsReportPage />} />
      <Route path="queue_statistics" element={<EntityReportPage report="queue_statistics" endpoint="/reports/queue/statistic" />} />
      <Route path="ring_groups_statistics" element={<EntityReportPage report="ring_group_statistics" endpoint="/reports/ring-groups/statistic" />} />
      <Route path="quality" element={<QualityReportPage />} />
    </Routes>
  );
}

function OverviewReportPage(): JSX.Element {
  const [state, setState] = useReportState();
  const query = useQuery({
    queryKey: ['reports', 'overview', state],
    queryFn: () => api.get<OverviewReport>(`/reports/overview${buildReportSearch(state)}`),
  });
  return (
    <ReportPage title="Overview" report="overview" state={state} onState={setState}>
      <QueryView query={query}>
        {(data) => (
          <>
            <SummaryCards summary={data.summary} />
            <TalkTimeBreakdown buckets={data.summary.answeredByTalkTime} />
            <DailyOverviewChart title="Overview" data={data.daily} />
            <TalkTimeBars title="Answered talk time per day" data={data.daily} />
            <HourlyStackedBars title="Calls during the day" data={data.hourly} />
            <TalkTimeBars title="Answered talk time during the day" data={data.hourly} />
            <div className="grid gap-4 lg:grid-cols-2">
              <WeekdayBars title="Calls by day of the week" data={data.daily} />
            </div>
            <UserStatsTable rows={data.users} />
          </>
        )}
      </QueryView>
    </ReportPage>
  );
}

function UserStatisticsReportPage(): JSX.Element {
  const [state, setState] = useReportState();
  const query = useQuery({
    queryKey: ['reports', 'user-statistics', state],
    queryFn: () => api.get<UserStatisticsReport>(`/reports/calls/statistics/users${buildReportSearch(state)}`),
  });
  return (
    <ReportPage title="User statistics" report="user_statistics" state={state} onState={setState}>
      <QueryView query={query}>
        {(data) => (
          <>
            <TalkTimeBreakdown buckets={data.summary.answeredByTalkTime} />
            <DailyOverviewChart title="Overview" data={data.daily} />
            <TalkTimeBars title="Answered talk time per day" data={data.daily} />
            <HourlyStackedBars title="Calls during the day" data={data.hourly} />
            <div className="grid gap-4 lg:grid-cols-2">
              <WeekdayBars title="Calls by day of the week" data={data.daily} />
            </div>
            <UserDurationBars title="Average duration" data={data.users} mode="average" />
            <UserDurationBars title="Total duration" data={data.users} mode="total" />
            <UserStatsTable rows={data.users} />
            <UserStackedBars title="Total calls" data={data.users} />
          </>
        )}
      </QueryView>
    </ReportPage>
  );
}

function MonthlyVolumeReportPage(): JSX.Element {
  const [state, setState] = useReportState();
  const query = useQuery({
    queryKey: ['reports', 'monthly-volume', state],
    queryFn: () => api.get<MonthlyCallVolumeReport>(`/reports/monthly-call-volume${buildReportSearch(state)}`),
  });
  return (
    <ReportPage title="Monthly call volume" report="monthly_call_volume" state={state} onState={setState} tabs={false}>
      <QueryView query={query} skeleton={<><ChartSkeleton /><ChartSkeleton /></>}>
        {(data) => (
          <>
            <MonthlyVolumeChart title="Inbound calls over the last 6 months" data={data.inbound} />
            <MonthlyVolumeChart title="Outbound calls over the last 6 months" data={data.outbound} />
            <ChartLegend items={[
              { color: monthlyVolumeColors.answered, label: 'Answered' },
              { color: monthlyVolumeColors.busy, label: 'Busy' },
              { color: monthlyVolumeColors.noAnswer, label: 'No answer' },
            ]} />
          </>
        )}
      </QueryView>
    </ReportPage>
  );
}

function CallHistoryReportPage(): JSX.Element {
  const [state, setState] = useReportState();
  return <CallsReportTemplate title="Call history" report="call_history" endpoint="/reports/calls" state={state} onState={setState} />;
}

function CallRecordingsReportPage(): JSX.Element {
  const [state, setState] = useReportState();
  return <CallsReportTemplate title="Call recordings" report="call_recordings" endpoint="/reports/call-recordings" state={state} onState={setState} defaultRecording />;
}

function CallsReportTemplate({ title, report, endpoint, state, onState, defaultRecording }: { title: string; report: ReportKind; endpoint: string; state: ReportQueryState; onState: (next: ReportQueryState) => void; defaultRecording?: boolean }): JSX.Element {
  const [offset, setOffset] = useState(0);
  const effective = defaultRecording ? { ...state, hasRecording: state.hasRecording || 'true' } : state;
  const query = useQuery({
    queryKey: ['reports', report, effective, offset],
    queryFn: () => api.get<PaginatedReport<CallReportRow>>(`${endpoint}${buildReportSearch(effective, { limit: 50, offset })}`),
  });
  // Totals for the whole filtered set, not just the page on screen.
  const summaryQuery = useQuery({
    queryKey: ['reports', report, 'summary', effective],
    queryFn: () => api.get<ReportSummary>(`/reports/calls/summary${buildReportSearch(effective)}`),
  });

  useEffect(() => {
    setOffset(0);
  }, [
    effective.from, effective.to, effective.timeFrom, effective.timeTo, effective.tab,
    effective.phone, effective.agentId, effective.campaignId, effective.queueId,
    effective.ringGroupId, effective.twilioNumberId, effective.direction, effective.status,
    effective.hasRecording, effective.minDuration, effective.maxDuration,
  ]);

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const activeFilters = countActiveFilters(state);
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + 50, total);

  return (
    <ReportPage title={title} report={report} state={state} onState={onState}>
      {query.isError ? (
        <ErrorState message={(query.error as { code?: string })?.code ?? "We couldn't load this report."} onRetry={() => void query.refetch()} />
      ) : (
        <>
        {summaryQuery.isLoading ? <SummaryCardsSkeleton /> : (
          <>
            <SummaryCards summary={summaryQuery.data} />
            <TalkTimeBreakdown buckets={summaryQuery.data?.answeredByTalkTime} />
          </>
        )}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2">Started</th>
                  <th className="px-4 py-2">Direction</th>
                  <th className="px-4 py-2">From</th>
                  <th className="px-4 py-2">To</th>
                  <th className="px-4 py-2">Lead</th>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Campaign</th>
                  <th className="px-4 py-2">Queue</th>
                  <th className="px-4 py-2">Ring group</th>
                  <th className="px-4 py-2">Duration</th>
                  <th className="px-4 py-2">Outcome</th>
                  <th className="px-4 py-2">Summary</th>
                  <th className="px-4 py-2">Recording</th>
                </tr>
              </thead>
              <tbody>
                {query.isLoading && <TableSkeleton rows={6} cols={13} />}
                {!query.isLoading && items.length === 0 && (
                  <EmptyTableRow
                    colSpan={13}
                    hasFilters={activeFilters > 0}
                    onClear={() => onState(clearFilters(state))}
                  />
                )}
                {items.map((row) => (
                  <tr key={row.id} className="border-t border-ink-100 hover:bg-ink-50">
                    <td className="px-4 py-2 text-ink-600">{new Date(row.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2"><DirectionBadge direction={row.direction} /></td>
                    <td className="px-4 py-2 font-mono text-ink-700">{row.fromNumber}</td>
                    <td className="px-4 py-2 font-mono text-ink-700">{row.toNumber}</td>
                    <td className="px-4 py-2">{row.leadName ?? <Empty />}</td>
                    <td className="px-4 py-2">{row.agentName ?? <Empty />}</td>
                    <td className="px-4 py-2">{row.campaignName ?? <Empty />}</td>
                    <td className="px-4 py-2">{row.queueName ?? <Empty />}</td>
                    <td className="px-4 py-2">{row.ringGroupName ?? <Empty />}</td>
                    <td className="px-4 py-2 font-mono tabular-nums">{formatDuration(row.durationSeconds)}</td>
                    <td className="px-4 py-2"><StatusBadge status={row.status} disposition={row.disposition} /></td>
                    <td className="w-96 max-w-96 whitespace-normal px-4 py-2 align-top text-ink-600">
                      <CallSummaryCell summary={row.summary} />
                    </td>
                    <td className="px-4 py-2 min-w-48">
                      {row.hasRecording ? <CallRecordingPlayer callId={row.id} hasRecording /> : <Empty />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-200 px-4 py-3 text-sm text-ink-600">
            <span className="tabular-nums">
              {total === 0 ? 'No results' : `${showingFrom.toLocaleString()}–${showingTo.toLocaleString()} of ${total.toLocaleString()}`}
            </span>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>Previous</button>
              <button type="button" className="btn-secondary" disabled={offset + 50 >= total} onClick={() => setOffset(offset + 50)}>Next</button>
            </div>
          </footer>
        </div>
        </>
      )}
    </ReportPage>
  );
}

function QualityReportPage(): JSX.Element {
  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-500">Reports / Quality</p>
        <h1 className="text-2xl font-semibold">Quality</h1>
      </header>
      <ReportTabs />
      <QualityScoresView />
    </div>
  );
}

function EntityReportPage({ report, endpoint }: { report: 'queue_statistics' | 'ring_group_statistics'; endpoint: string }): JSX.Element {
  const [state, setState] = useReportState();
  const query = useQuery({
    queryKey: ['reports', report, state],
    queryFn: () => api.get<EntityStatisticsReport>(`${endpoint}${buildReportSearch(state)}`),
  });
  return (
    <ReportPage title={report === 'queue_statistics' ? 'Queue statistics' : 'Ring group statistics'} report={report} state={state} onState={setState}>
      <QueryView query={query}>
        {(data) => (
          <>
            <SummaryCards summary={data.summary} />
            <TalkTimeBreakdown buckets={data.summary.answeredByTalkTime} />
            <EntityTable rows={data.entities} />
            <LineChart title="Calls distribution" data={data.distribution} />
            <TalkTimeBars title="Answered talk time" data={data.distribution} />
            <LineChart title="Calls answer time distribution" data={data.answerTimeDistribution} />
            <UserStatsTable rows={data.users} />
          </>
        )}
      </QueryView>
    </ReportPage>
  );
}

function ReportPage({ title, report, state, onState, tabs = true, children }: { title: string; report: ReportKind; state: ReportQueryState; onState: (next: ReportQueryState) => void; tabs?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-500">Reports / {reportLabel(report)}</p>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>
      <ReportTabs />
      <ReportControls report={report} state={state} onChange={onState} tabs={tabs} />
      {children}
    </div>
  );
}

function QueryView<T>({ query, skeleton, children }: { query: UseQueryResult<T>; skeleton?: React.ReactNode; children: (data: T) => React.ReactNode }): JSX.Element {
  if (query.isError) {
    return <ErrorState message={(query.error as { code?: string })?.code ?? "We couldn't load this report."} onRetry={() => void query.refetch()} />;
  }
  if (query.isLoading || !query.data) {
    return <>{skeleton ?? <DefaultReportSkeleton />}</>;
  }
  return <>{children(query.data)}</>;
}

function DefaultReportSkeleton(): JSX.Element {
  return (
    <>
      <SummaryCardsSkeleton />
      <ChartSkeleton />
      <ChartSkeleton />
    </>
  );
}

function SummaryCards({ summary }: { summary?: ReportSummary }): JSX.Element {
  const values: { label: string; value: React.ReactNode; accent?: string }[] = [
    { label: 'Total calls', value: summary?.totalCalls ?? 0 },
    { label: 'Inbound', value: summary?.inboundCalls ?? 0 },
    { label: 'Outbound', value: summary?.outboundCalls ?? 0 },
    { label: 'Answered', value: summary?.answered ?? 0, accent: 'text-emerald-700' },
    { label: 'Missed/Not answered', value: summary?.missedOrNotAnswered ?? 0, accent: 'text-red-700' },
    { label: 'Total duration', value: formatDuration(summary?.totalDurationSeconds ?? 0) },
    { label: 'Average duration', value: formatDuration(summary?.averageDurationSeconds ?? 0) },
    { label: 'Recordings', value: summary?.recordings ?? 0, accent: 'text-brand-700' },
  ];
  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {values.map(({ label, value, accent }) => (
        <div key={label} className="card p-3">
          <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
          <div className={`mt-1 text-xl font-semibold tabular-nums ${accent ?? 'text-ink-800'}`}>{value}</div>
        </div>
      ))}
    </section>
  );
}

// `answeredByTalkTime` is an object, so it can never be a sort key itself — the
// sortable talk-time columns are the cumulative "at least this long" counts.
type SortableUserKey =
  | Exclude<keyof UserStatisticRow, 'answeredByTalkTime'>
  | `atLeast:${TalkTimeBucketKey}`;

function userSortValue(row: UserStatisticRow, key: SortableUserKey): string | number {
  if (key.startsWith('atLeast:')) {
    return talkTimeAtLeast(row.answeredByTalkTime, key.slice('atLeast:'.length) as TalkTimeBucketKey);
  }
  return row[key as Exclude<keyof UserStatisticRow, 'answeredByTalkTime'>] ?? '';
}

function UserStatsTable({ rows }: { rows: UserStatisticRow[] }): JSX.Element {
  const [sortBy, setSortBy] = useState<SortableUserKey>('numberOfCalls');
  const [desc, setDesc] = useState(true);
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = userSortValue(a, sortBy);
    const bv = userSortValue(b, sortBy);
    const result = av > bv ? 1 : av < bv ? -1 : 0;
    return desc ? -result : result;
  }), [desc, rows, sortBy]);
  const onSort = (key: SortableUserKey): void => {
    setDesc(sortBy === key ? !desc : true);
    setSortBy(key);
  };
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2">
        <h2 className="text-sm font-semibold text-ink-700">User statistics</h2>
        <span className="text-xs text-ink-500">Click a column header to sort</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full whitespace-nowrap text-sm">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <SortHeader label="User" sortKey="user" current={sortBy} desc={desc} onSort={onSort} />
              <SortHeader label="Number of calls" sortKey="numberOfCalls" current={sortBy} desc={desc} onSort={onSort} numeric />
              <SortHeader label="Answered" sortKey="answered" current={sortBy} desc={desc} onSort={onSort} numeric />
              {CUMULATIVE_BUCKETS.map((bucket) => (
                <SortHeader
                  key={bucket.key}
                  label={`Talked ${bucket.atLeastLabel}`}
                  sortKey={`atLeast:${bucket.key}`}
                  current={sortBy}
                  desc={desc}
                  onSort={onSort}
                  numeric
                />
              ))}
              <SortHeader label="Missed/Not answered" sortKey="missedOrNotAnswered" current={sortBy} desc={desc} onSort={onSort} numeric />
              <SortHeader label="Total duration" sortKey="totalDurationSeconds" current={sortBy} desc={desc} onSort={onSort} numeric />
              <SortHeader label="Average duration" sortKey="averageDurationSeconds" current={sortBy} desc={desc} onSort={onSort} numeric />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <SimpleEmptyRow colSpan={6 + CUMULATIVE_BUCKETS.length} text="No user statistics for this period." />}
            {sorted.map((row) => (
              <tr key={row.userId ?? row.user} className="border-t border-ink-100 hover:bg-ink-50">
                <td className="px-4 py-2 font-medium text-ink-800">{row.user}</td>
                <td className="px-4 py-2 tabular-nums">{row.numberOfCalls}</td>
                <td className="px-4 py-2 tabular-nums text-emerald-700">{row.answered}</td>
                {CUMULATIVE_BUCKETS.map((bucket) => (
                  <td key={bucket.key} className="px-4 py-2 tabular-nums text-ink-700">
                    {talkTimeAtLeast(row.answeredByTalkTime, bucket.key)}
                  </td>
                ))}
                <td className="px-4 py-2 tabular-nums text-red-700">{row.missedOrNotAnswered}</td>
                <td className="px-4 py-2 font-mono tabular-nums">{formatDuration(row.totalDurationSeconds)}</td>
                <td className="px-4 py-2 font-mono tabular-nums">{formatDuration(row.averageDurationSeconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntityTable({ rows }: { rows: EntityStatisticsReport['entities'] }): JSX.Element {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full whitespace-nowrap text-sm">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Number of calls</th>
              <th className="px-4 py-2">Answered</th>
              {CUMULATIVE_BUCKETS.map((bucket) => (
                <th key={bucket.key} className="px-4 py-2">Talked {bucket.atLeastLabel}</th>
              ))}
              <th className="px-4 py-2">Missed/Not answered</th>
              <th className="px-4 py-2">Busy</th>
              <th className="px-4 py-2">No answer</th>
              <th className="px-4 py-2">Total duration</th>
              <th className="px-4 py-2">Average duration</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <SimpleEmptyRow colSpan={8 + CUMULATIVE_BUCKETS.length} text="No statistics for this period." />}
            {rows.map((row) => (
              <tr key={row.id ?? row.name} className="border-t border-ink-100 hover:bg-ink-50">
                <td className="px-4 py-2 font-medium text-ink-800">{row.name}</td>
                <td className="px-4 py-2 tabular-nums">{row.numberOfCalls}</td>
                <td className="px-4 py-2 tabular-nums text-emerald-700">{row.answered}</td>
                {CUMULATIVE_BUCKETS.map((bucket) => (
                  <td key={bucket.key} className="px-4 py-2 tabular-nums text-ink-700">
                    {talkTimeAtLeast(row.answeredByTalkTime, bucket.key)}
                  </td>
                ))}
                <td className="px-4 py-2 tabular-nums text-red-700">{row.missedOrNotAnswered}</td>
                <td className="px-4 py-2 tabular-nums">{row.busy}</td>
                <td className="px-4 py-2 tabular-nums">{row.noAnswer}</td>
                <td className="px-4 py-2 font-mono tabular-nums">{formatDuration(row.totalDurationSeconds)}</td>
                <td className="px-4 py-2 font-mono tabular-nums">{formatDuration(row.averageDurationSeconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface SortHeaderProps {
  label: string;
  sortKey: SortableUserKey;
  current: SortableUserKey;
  desc: boolean;
  numeric?: boolean;
  onSort: (key: SortableUserKey) => void;
}

function SortHeader({ label, sortKey, current, desc, numeric, onSort }: SortHeaderProps): JSX.Element {
  const active = current === sortKey;
  const ariaSort = active ? (desc ? 'descending' : 'ascending') : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`px-4 py-2 ${numeric ? 'text-left' : ''} hover:bg-ink-100`}
    >
      <button
        type="button"
        className="inline-flex w-full items-center gap-1 font-semibold uppercase tracking-wide"
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <SortIcon active={active} desc={desc} />
      </button>
    </th>
  );
}

function SortIcon({ active, desc }: { active: boolean; desc: boolean }): JSX.Element {
  const upActive = active && !desc;
  const downActive = active && desc;
  return (
    <svg width="9" height="13" viewBox="0 0 9 13" className="inline-block flex-shrink-0" aria-hidden="true">
      <path d="M4.5 0 L9 4.5 L0 4.5 Z" fill={upActive ? '#334155' : '#cbd5e1'} />
      <path d="M4.5 13 L0 8.5 L9 8.5 Z" fill={downActive ? '#334155' : '#cbd5e1'} />
    </svg>
  );
}

function DirectionBadge({ direction }: { direction: 'inbound' | 'outbound' }): JSX.Element {
  const styles = direction === 'inbound'
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-brand-100 text-brand-700';
  return <span className={`badge capitalize ${styles}`}>{direction}</span>;
}

function StatusBadge({ status, disposition }: { status: CallStatus; disposition: string | null }): JSX.Element {
  const label = disposition ?? status.replace(/_/g, ' ');
  const styles = STATUS_STYLES[status] ?? 'bg-ink-100 text-ink-700';
  return <span className={`badge capitalize ${styles}`}>{label}</span>;
}

const STATUS_STYLES: Record<CallStatus, string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  busy: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  no_answer: 'bg-ink-100 text-ink-700',
  canceled: 'bg-ink-100 text-ink-700',
  in_progress: 'bg-amber-100 text-amber-800',
  ringing: 'bg-amber-100 text-amber-800',
  initiated: 'bg-amber-100 text-amber-800',
  queued: 'bg-amber-100 text-amber-800',
};

function EmptyTableRow({ colSpan, hasFilters, onClear }: { colSpan: number; hasFilters: boolean; onClear: () => void }): JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-ink-500">
        {hasFilters ? (
          <div className="space-y-2">
            <div>No calls match these filters. Try expanding the date range or clearing filters.</div>
            <button type="button" className="btn-secondary" onClick={onClear}>Clear filters</button>
          </div>
        ) : (
          <span>No calls in this date range yet.</span>
        )}
      </td>
    </tr>
  );
}

function SimpleEmptyRow({ colSpan, text }: { colSpan: number; text: string }): JSX.Element {
  return <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-ink-500">{text}</td></tr>;
}

function Empty(): JSX.Element {
  return <span className="text-ink-300">—</span>;
}

const SUMMARY_PREVIEW_CHARS = 160;

// AI summaries run to a paragraph. Showing them in full would make every row
// tall enough to bury the rest of the table, so they clamp until asked for.
function CallSummaryCell({ summary }: { summary: string | null }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  if (!summary) return <Empty />;
  if (summary.length <= SUMMARY_PREVIEW_CHARS) {
    return <span className="text-xs leading-relaxed">{summary}</span>;
  }
  return (
    <button
      type="button"
      className="text-left text-xs leading-relaxed hover:text-ink-800"
      aria-expanded={expanded}
      onClick={() => setExpanded(!expanded)}
    >
      {expanded ? summary : `${summary.slice(0, SUMMARY_PREVIEW_CHARS).trimEnd()}…`}
      <span className="ml-1 whitespace-nowrap font-medium text-brand-600">{expanded ? 'Less' : 'More'}</span>
    </button>
  );
}
